import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { providersApi, ProviderPublic } from '../services/providers';

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const [providers, setProviders] = useState<ProviderPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchProviders = async () => {
      if (!query) {
        setProviders([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const results = await providersApi.searchProviders(query);
        setProviders(results);
      } catch (err) {
        console.error('Error searching providers:', err);
        setError('Failed to search providers');
      } finally {
        setLoading(false);
      }
    };

    searchProviders();
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f6f2] py-4 sm:py-6 lg:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="animate-pulse">
            <div className="h-6 sm:h-8 bg-gray-200 rounded w-2/3 sm:w-1/3 mb-4"></div>
            <div className="space-y-3 sm:space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-lg p-4 sm:p-6 shadow">
                  <div className="h-5 sm:h-6 bg-gray-200 rounded w-3/4 sm:w-1/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full sm:w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f6f2] py-4 sm:py-6 lg:py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-4 sm:mb-6 text-[#c6a664] hover:text-[#b8963e] flex items-center gap-2 font-['Open_Sans'] min-h-[44px] text-sm sm:text-base"
        >
          ← Back to Dashboard
        </button>

        <h1 className="text-2xl sm:text-3xl font-['Playfair_Display'] text-[#1f1f1f] mb-2">Search Results</h1>
        <p className="text-sm sm:text-base text-[#767472] font-['Open_Sans'] mb-6 sm:mb-8">
          {providers.length} results for "{query}"
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {providers.length === 0 && !error && (
          <div className="bg-white rounded-lg p-8 text-center">
            <p className="text-[#767472] font-['Open_Sans']">
              No providers found matching your search.
            </p>
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className="bg-white rounded-lg p-4 sm:p-6 shadow hover:shadow-lg transition-shadow cursor-pointer active:bg-gray-50"
              onClick={() => navigate(`/provider/${provider.id}`)}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-['Playfair_Display'] text-[#1f1f1f] mb-1">
                    {provider.business_name}
                  </h3>
                  <p className="text-[#767472] font-['Open_Sans'] text-xs sm:text-sm mb-2">
                    {[provider.city, provider.state].filter(Boolean).join(', ')}
                  </p>
                  {provider.specialties && provider.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                      {provider.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-2 sm:px-3 py-1 bg-[#f8f6f2] text-[#c6a664] text-xs font-['Open_Sans'] rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                  {provider.bio && (
                    <p className="text-[#767472] font-['Open_Sans'] text-xs sm:text-sm line-clamp-2">
                      {provider.bio}
                    </p>
                  )}
                </div>
                <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start sm:ml-6 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                  <div className="flex items-center sm:block gap-2">
                    <div className="text-xl sm:text-2xl font-bold font-['Open_Sans'] text-[#c6a664]">
                      {provider.clout_score}
                    </div>
                    <div className="text-xs text-[#767472] font-['Open_Sans']">CLOUT Score</div>
                  </div>
                  {provider.is_certified === 1 && (
                    <div className="mt-0 sm:mt-2 text-xs text-green-600 font-['Open_Sans']">
                      ✓ Certified
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
